import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../components/auth/AuthContext';
import {
  ArrowRightLeft,
  CalendarClock,
  TrendingUp,
  BarChart3,
  Settings,
  RefreshCw,
} from 'lucide-react';
import '../HomePage.css';

interface TileConfig {
  id: string;
  titleKey: string;
  descriptionKey: string;
  icon: React.ReactNode;
  path: string | null;
  roles: string[];
  subItems?: { titleKey: string; path: string }[];
}

const CommodityHomePage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Use the Commodities module role for tile visibility
  const moduleRole = user?.moduleRoles?.Commodities;

  const tiles: TileConfig[] = [
    {
      id: 'commodity-trade-booking',
      titleKey: 'navigation.commodityTradeBooking',
      descriptionKey: 'home.commodityTradeBookingDesc',
      icon: <ArrowRightLeft size={48} />,
      path: '/commodities/trade-booking',
      roles: ['client_admin', 'client_fo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user'],
    },
    {
      id: 'commodity-valuation',
      titleKey: 'navigation.commodityValuation',
      descriptionKey: 'home.commodityValuationDesc',
      icon: <TrendingUp size={48} />,
      path: '/commodities/valuation',
      roles: ['client_admin', 'client_fo_user', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user'],
    },
    {
      id: 'commodity-expiries',
      titleKey: 'navigation.commodityExpiry',
      descriptionKey: 'home.commodityExpiriesDesc',
      icon: <CalendarClock size={48} />,
      path: '/commodities/expiry',
      roles: ['client_admin', 'client_fo_user', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user'],
    },
    {
      id: 'commodity-modifications',
      titleKey: 'navigation.commodityModifications',
      descriptionKey: 'home.commodityModificationsDesc',
      icon: <RefreshCw size={48} />,
      path: '/commodities/modifications',
      roles: ['client_admin', 'client_fo_user', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user'],
    },
    {
      id: 'commodity-documents-reports',
      titleKey: 'navigation.documentsAndReports',
      descriptionKey: 'home.commodityDocumentsAndReportsDesc',
      icon: <BarChart3 size={48} />,
      path: null,
      roles: ['client_admin', 'client_fo_user', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user'],
      subItems: [
        { titleKey: 'navigation.customReports', path: '/commodities/custom-reports' },
        { titleKey: 'navigation.positions', path: '/commodities/positions' },
      ],
    },
    {
      id: 'commodity-administration',
      titleKey: 'navigation.administration',
      descriptionKey: 'home.commodityAdministrationDesc',
      icon: <Settings size={48} />,
      path: null,
      roles: ['client_admin'],
      subItems: [
        { titleKey: 'commodities.admin.tabs.subscriptionImportConfigs', path: '/commodities/admin?tab=subscriptionImportConfigs' },
        { titleKey: 'commodities.admin.tabs.expiryImportConfigs', path: '/commodities/admin?tab=expiryImportConfigs' },
      ],
    },
  ];

  const visibleTiles = tiles.filter(tile =>
    moduleRole && tile.roles.includes(moduleRole)
  );

  const mainTiles = visibleTiles.filter(tile =>
    ['commodity-trade-booking', 'commodity-expiries', 'commodity-valuation', 'commodity-modifications'].includes(tile.id)
  );
  const generalTiles = visibleTiles.filter(tile =>
    ['commodity-documents-reports', 'commodity-administration'].includes(tile.id)
  );

  const handleTileClick = (tile: TileConfig) => {
    if (!tile.path) return;
    navigate(tile.path);
  };

  const handleSubItemClick = (path: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(path);
  };

  const renderTile = (tile: TileConfig) => {
    const isDisabled = !tile.path && !tile.subItems;

    return (
      <div
        key={tile.id}
        className={`home-tile ${isDisabled ? 'disabled' : ''} ${tile.subItems ? 'has-subitems' : ''}`}
        onClick={() => handleTileClick(tile)}
      >
        <div className="tile-icon">
          {tile.icon}
        </div>
        <h3 className="tile-title">{t(tile.titleKey)}</h3>
        <p className="tile-description">{t(tile.descriptionKey)}</p>
        {tile.subItems && (
          <div className="tile-subitems">
            {tile.subItems.map((subItem, index) => (
              <button
                key={index}
                className="tile-subitem"
                onClick={(e) => handleSubItemClick(subItem.path, e)}
              >
                {t(subItem.titleKey)}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="home-container">
      <div className="home-header">
        <h1>{t('home.commoditiesWelcome')}</h1>
        <p>{t('home.commoditiesSubtitle')}</p>
      </div>

      {mainTiles.length > 0 && (
        <div className="tile-section">
          <h2 className="section-title">{t('home.tradeLifecycle')}</h2>
          <div className="tile-row" style={{ gridTemplateColumns: `repeat(${mainTiles.length}, 1fr)` }}>
            {mainTiles.map(renderTile)}
          </div>
        </div>
      )}

      {generalTiles.length > 0 && (
        <div className="tile-section">
          <h2 className="section-title">{t('home.general')}</h2>
          <div className="tile-row general-row">
            {generalTiles.map(renderTile)}
          </div>
        </div>
      )}
    </div>
  );
};

export default CommodityHomePage;
